import { useAuthStore } from '@/stores/auth'
import { useSyncStore } from '@/stores/sync'
import axios, { type CreateAxiosDefaults } from 'axios'

/** Get the axios instance for communicating with the Purchase API */
export function getPurchaseApiAxiosInstance(options: CreateAxiosDefaults = {}) {
	const instance = axios.create({
		baseURL: import.meta.env.VITE_PURCHASE_API_URL + '/api/',
		withCredentials: true,
		...options,
	})

	/** In local environment, add pUserToken to request from auth token */
	instance.interceptors.request.use(async (request) => {
		const auth = useAuthStore()
		const token = await auth.getAuthToken()

		request.headers.set('X-PAPI-Authenticated-With', token)
		return request
	})

	instance.interceptors.response.use(null, (error) => {
		if (error.response?.status === 401) {
			const auth = useAuthStore()
			if (auth.user) auth.sessionExpired = true
		}

		return Promise.reject(error)
	})

	// Update offline status and show errors based on the result of the axios request
	instance.interceptors.response.use(
		(response) => {
			const sync = useSyncStore()
			const url = response.config.url ?? ''

			// Clear server error if it existed and now succeeded
			if (sync.serverErrors[url]) {
				sync.clearServerError(url)
			}

			// Any successful response means we're online
			sync.offline = false
			return response
		},
		(error) => {
			const sync = useSyncStore()
			const url = error.config?.url ?? ''

			if (axios.isAxiosError(error)) {
				const res = error.response

				if (!res) {
					// No response means network failure (offline)
					sync.offline = true
				} else if (res.status === 401) {
					// Skip showing 401 (unauthorized) errors because we already have an alert prompting them to log back in
					sync.offline = false
				} else if (res.status >= 400) {
					// Server error (still online, but store/show error)
					sync.offline = false
					sync.setServerError(url, res.status, res.data?.message)
				}
			}

			return Promise.reject(error)
		}
	)

	return instance
}

export const purchaseApiAxiosInstance = getPurchaseApiAxiosInstance()
